import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, catchError, map, of, switchMap, timeout } from 'rxjs';

import {
  FeatureFlag,
  FeatureFlagService,
} from '@mimecast-ui/shared/data-access-core/feature-flags';

import { LoginSessionService } from './login-session.service';

export const MC_LOCALE_COOKIE = 'mc-locale';
/** Matches unused legacy MUL httpTimeoutInterceptorService budget (10s). */
export const LOCALE_BRIDGE_TIMEOUT_MS = 10_000;
const LOCALE_COOKIE_EXPIRY_MS = 12 * 60 * 60 * 1000;
const WHOAMI_URL = '/bridge/identity/whoami';
const FALLBACK_LOCALE_PREFS = 'auto';

interface WhoamiResponse {
  'account-codes'?: { cg?: string };
}

interface LocalePreferenceResponse {
  value?: string;
}

/**
 * After successful MUL login, optionally syncs OneMC locale preference into
 * the mc-locale cookie. Never blocks or fails login.
 */
@Injectable({ providedIn: 'root' })
export class LocalePreferenceService {
  private readonly http = inject(HttpClient);
  private readonly cookieService = inject(CookieService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly loginSessionService = inject(LoginSessionService);

  /**
   * When flag off: completes immediately with no HTTP/cookie.
   * When flag on: write fallback 'auto', then fetch preference and overwrite.
   * Errors and timeouts never throw; fallback 'auto' remains if a later step fails.
   */
  applyAfterLogin(): Observable<void> {
    if (
      !this.featureFlagService.getBooleanFlag(
        FeatureFlag.ROLLOUT_MUL_ONEMC_LOCALIZATION,
        false
      )
    ) {
      return of(undefined);
    }

    this.writeLocaleCookie(FALLBACK_LOCALE_PREFS);

    return this.http.get<WhoamiResponse>(WHOAMI_URL).pipe(
      switchMap((whoami) => {
        const cg = whoami['account-codes']?.cg;
        if (!cg) {
          return of(undefined);
        }
        return this.http
          .get<LocalePreferenceResponse>(
            `/bridge/accounts/${cg}/user-preferences/v1/locale`
          )
          .pipe(
            map((prefsData) => {
              const localePrefs = JSON.parse(prefsData.value ?? '');
              if (localePrefs) {
                this.writeLocaleCookie(localePrefs);
              }
            })
          );
      }),
      timeout(LOCALE_BRIDGE_TIMEOUT_MS),
      catchError(() => of(undefined)),
      map(() => undefined)
    );
  }

  private writeLocaleCookie(prefs: unknown): void {
    const expires = new Date(Date.now() + LOCALE_COOKIE_EXPIRY_MS);
    const sameSite = this.loginSessionService.resolveLocaleCookieSameSite();
    const value = JSON.stringify(prefs);

    this.cookieService.set(
      MC_LOCALE_COOKIE,
      value,
      sameSite === undefined
        ? {
            expires,
            path: '/',
            secure: true,
          }
        : {
            expires,
            path: '/',
            secure: true,
            sameSite,
          }
    );
  }
}
